import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import type { FC, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import debounce from 'lodash/debounce';

import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Inline from '@atlaskit/primitives/inline';
import Pressable from '@atlaskit/primitives/pressable';
import { xcss } from '@atlaskit/primitives/xcss';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { ContentMode } from '@confluence/content-types-utils/entry-points/getContentMode.ts';
import { isCanvasContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import type { ContentType as ShareContentType } from '@confluence/share-and-restrict-dialog/entry-points/ShareRestrictAndCopyCoordinator.tsx';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { Link } from '@confluence/route-manager/entry-points/Link.ts';
import { useSidePanelStateActions } from '@confluence/side-panel-state-container';
import { useFullPageEditorApiState } from '@confluence/full-page-editor-store';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useUpdateLastEditedTimestampVersionCache } from '@confluence/content-types-utils/entry-points/useUpdateLastEditedTimestampVersionCache.ts';
import { useCollabNetworkStatus } from '@confluence/collab-network-status';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { useNewContentHistoryActions } from '@confluence/content-history-new/entry-points/useNewContentHistoryActions.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import { ToolTipOverride } from '../ContentTypesTitle.tsx';
import {
	lastEditedHeaderFriendlyFormatter,
	lastEditedTooltipTimestampFormatter,
	formatHeaderMessage,
	formatTooltipMessage,
} from '../LastEditedUtils.tsx';

const lastEditedStyles = xcss({
	color: 'color.text.subtle',
	alignItems: 'center',
	flexDirection: 'row',
	padding: 'space.050',
	textWrap: 'nowrap',
});

const linkStyles = {
	color: token('color.text.subtle'),
};

const disabledLinkStyles = {
	...linkStyles,
	pointerEvents: 'none',
	cursor: 'default',
};

const pressableStyles = xcss({
	color: 'color.text.subtle',
	font: 'font.body',
	':hover': {
		textDecoration: 'underline',
	},
});

export type LastEditedUser = {
	fullName: string;
};

export type LastEditedCurrentUser = {
	fullName: string;
	typename: string;
};

export type LastEditedVersion = {
	by: LastEditedUser;
	contentTypeModified: boolean;
	number: number;
	when: string;
	friendlyWhen: string;
};

export type LastEditedProps = {
	contentId: string;
	version: LastEditedVersion;
	contentType: ShareContentType;
	contentMode: ContentMode | undefined;
	spaceKey: string;
	hasEditPermission: boolean;
};

export const LastEdited: FC<LastEditedProps> = ({
	contentId,
	version,
	contentType,
	contentMode,
	spaceKey,
}) => {
	const { showSidePanel } = useSidePanelStateActions();
	const { formatMessage, formatTime, formatDate } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ isCollabOffline }] = useCollabNetworkStatus();

	// Network status is used when:
	// - isCollabOffline: The collab provider has detected a network disconnection
	// - platform_editor_offline_editing_web: Parent experiment for offline editing experience (must be enabled)
	// - platform_editor_offline_editing_wrapper_override: Kill switch to disable this feature (must be disabled)
	const isNetworkDisabled =
		isCollabOffline &&
		expVal('platform_editor_offline_editing_web', 'isEnabled', false) &&
		!fg('platform_editor_offline_editing_wrapper_override');
	const previousRemoteChangeTimeRef = useRef<number | null | undefined>(undefined);
	const previousLocalChangeTimeRef = useRef<number | null | undefined>(undefined);
	const updateTimestampCache = useUpdateLastEditedTimestampVersionCache();
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();
	const contentIdRef = useRef(contentId);
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const { setWasTransitionedFromEditor } = useNewContentHistoryActions();
	const isLivePage = useIsCurrentPageLive();

	// Debounce to avoid rapid calls and prevent performance lag
	const debouncedSetRecentVersion = useMemo(
		() =>
			debounce(() => {
				updateTimestampCache({
					contentId: contentIdRef.current, // we need to use a ref for contentId so that the debounced timer doesn't reset every rerender
				});
			}, 3000),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[], // intentionally do not add dependencies so that the debounce timer is not reset on every rerender
	);

	// Update time stamp to "Just now" on page edit and live page edit
	usePluginStateEffect(fullPageEditorPresetAPI, ['collabEdit'], ({ collabEditState }) => {
		const lastRemoteChangeTime = collabEditState?.initialised?.lastRemoteOrganicBodyChangeAt;
		const lastLocalChangeTime = collabEditState?.initialised?.lastLocalOrganicBodyChangeAt;

		const hasRemoteChanged =
			lastRemoteChangeTime && lastRemoteChangeTime !== previousRemoteChangeTimeRef.current;
		const hasLocalChanged =
			lastLocalChangeTime && lastLocalChangeTime !== previousLocalChangeTimeRef.current;

		if (hasRemoteChanged || hasLocalChanged) {
			previousRemoteChangeTimeRef.current = lastRemoteChangeTime;
			previousLocalChangeTimeRef.current = lastLocalChangeTime;
			debouncedSetRecentVersion();
		}
	});

	useEffect(() => {
		contentIdRef.current = contentId;
	}, [contentId]);

	// Cancel any pending timers on unmount
	useEffect(() => {
		return () => {
			debouncedSetRecentVersion.cancel();
		};
	}, [debouncedSetRecentVersion]);

	const fireOnClickAnalyticsEvent = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'objectHeader',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'lastEditedTimestamp',
				attributes: {
					contentType,
					contentMode,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, contentType, contentMode]);

	const handleLinkClick = useCallback(() => {
		// Track when user navigates from editor to version history so we can redirect back to editor on back click
		// Only track for non-live pages when in edit mode since lives pages use the view page route
		const isEditMode = contentMode === 'edit';
		if (
			!isLivePage &&
			isEditMode &&
			expVal('confluence_content_history_updates', 'isEnabled', false)
		) {
			setWasTransitionedFromEditor(true);
		}

		fireOnClickAnalyticsEvent();
	}, [isLivePage, contentMode, setWasTransitionedFromEditor, fireOnClickAnalyticsEvent]);

	const createEditorChangeLink = (formattedMessage: ReactNode) => {
		return (
			<Link
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={isNetworkDisabled ? disabledLinkStyles : linkStyles}
				href={`${CONTEXT_PATH}/spaces/${spaceKey}/history/${contentId}`}
				onClick={isNetworkDisabled ? undefined : handleLinkClick}
			>
				{formattedMessage}
			</Link>
		);
	};

	const handleContentTypesLinkClick = () => {
		showSidePanel();
		fireOnClickAnalyticsEvent();
	};

	const createContentTypesChangeLink = (formattedMessage: ReactNode) => {
		return (
			<Pressable
				xcss={pressableStyles}
				backgroundColor="color.background.neutral.subtle"
				onClick={handleContentTypesLinkClick}
			>
				{formattedMessage}
			</Pressable>
		);
	};

	const { timeZone: userTimeZone, tenantTimeZone } = useSessionData();
	const timeZone = userTimeZone || tenantTimeZone || undefined;

	const lastEditedHeaderResult = useMemo(() => {
		const lastEditedTime = new Date(version.when);
		const isEditMode = !(contentMode === 'view' || contentMode === 'archived');
		const { headerTimestamp, isSavedIndicator } = lastEditedHeaderFriendlyFormatter(
			lastEditedTime,
			version.friendlyWhen,
			isEditMode,
			formatMessage,
			renderServerPlaceholder,
		);
		return { headerTimestamp, isSavedIndicator };
	}, [version.when, version.friendlyWhen, contentMode, formatMessage, renderServerPlaceholder]);

	const lastEditedTooltipResult = useMemo(() => {
		const lastEditedTime = new Date(version.when);
		return lastEditedTooltipTimestampFormatter(
			lastEditedTime,
			version.friendlyWhen,
			formatMessage,
			formatTime,
			formatDate,
			timeZone,
			renderServerPlaceholder,
		);
	}, [
		version.when,
		version.friendlyWhen,
		formatMessage,
		formatTime,
		formatDate,
		timeZone,
		renderServerPlaceholder,
	]);

	/**
	 * Formats header timestamp as well as tooltip timestamp
	 */
	const formatTimestamps = () => {
		let headerMessage: ReactNode | null;
		let tooltipMessage: ReactNode | null;

		const isEditMode = !(contentMode === 'view' || contentMode === 'archived');
		const { headerTimestamp, isSavedIndicator } = lastEditedHeaderResult;
		const tooltipTimestamp = lastEditedTooltipResult;

		const isPageOrBlog = contentType === 'page' || contentType === 'blogpost';
		if (isPageOrBlog) {
			const hasEditorVersionHistory = !!spaceKey && contentMode !== 'draft';

			headerMessage = formatHeaderMessage({
				localizedHeaderTimestamp: headerTimestamp,
				isSavedIndicator,
				hasVersionHistory: hasEditorVersionHistory,
				isPageOrBlog,
				isEditMode,
				versionNumber: version.number,
				createChangeLink: createEditorChangeLink,
				formatMessage,
			});

			tooltipMessage = formatTooltipMessage({
				localizedTooltipTimestamp: tooltipTimestamp,
				isEditMode,
				isPageOrBlog,
				versionNumber: version.number,
				fullName: version.by.fullName,
				formatMessage,
			});
		} else if (isCanvasContentType(contentType) || contentType === 'database') {
			const hasContentTypesVersionHistory = !version.contentTypeModified;

			headerMessage = formatHeaderMessage({
				localizedHeaderTimestamp: headerTimestamp,
				isSavedIndicator,
				hasVersionHistory: hasContentTypesVersionHistory,
				isPageOrBlog,
				isEditMode,
				versionNumber: version.number,
				createChangeLink: createContentTypesChangeLink,
				formatMessage,
			});

			tooltipMessage = formatTooltipMessage({
				localizedTooltipTimestamp: tooltipTimestamp,
				isEditMode,
				isPageOrBlog,
				versionNumber: version.number,
				fullName: version.by.fullName,
				formatMessage,
			});
		} else {
			headerMessage = null;
			tooltipMessage = null;
		}

		return { headerMessage, tooltipMessage };
	};

	// Ensures that timestamp will only display when most accurate timestamp is available
	if (contentMode === 'edit' && !version.when) {
		return null;
	}

	const { headerMessage: formattedHeaderMessage, tooltipMessage: formattedTooltipMessage } =
		formatTimestamps();

	// Only show tooltip if header timestamp is not full format > 2 days
	return (
		<Inline xcss={lastEditedStyles}>
			<Tooltip component={ToolTipOverride} content={formattedTooltipMessage} delay={0}>
				{formattedHeaderMessage}
			</Tooltip>
		</Inline>
	);
};
